// Copyright 2026. WebPros International GmbH. All rights reserved.

import { IPaginateApiResponse } from 'common/api/resources/Response';
import { AxiosPromise } from 'axios';
import {
    IAdditionalOptions,
    ILoadOptions,
    ISelectOption,
} from 'common/components/SelectInput';
import {
    Dispatch,
    SetStateAction,
} from 'react';
import { IPaginatedWithSearch } from 'common/api/resources/Request/request';
import { GroupBase } from 'react-select';

/**
 * Helper factory for creating options loader functions for async select input.
 *
 * @param api Function to fetch data.
 * @param mapper Function to map fetched data to select options.
 * @param allowed Flag to allow or disallow fetching data.
 * @param filters Filters for the request.
 * @param query Additional query parameters.
 */
export const createOptionsLoader = <
    TItem,
    Filters = unknown,
    TRequest extends IPaginatedWithSearch = IPaginatedWithSearch<Filters>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TOption = any,
>(
        api: (params?: TRequest) => AxiosPromise<IPaginateApiResponse<TItem[]>>,
        mapper: (item: TItem) => TOption,
        allowed: boolean = true,
        filters?: TRequest['filters'],
        query?: Omit<TRequest, 'filters'>
    ): ILoadOptions<TOption> =>
        async (search, _, additional? : IAdditionalOptions | undefined) => {
            const page = additional?.page ?? 1;
            if (!allowed) {
                return {
                    options: [],
                    hasMore: false,
                };
            }

            const {
                data: { data, links },
            } = await api({
                ...query,
                page,
                filters: {
                    ...filters,
                    search,
                },
            } as TRequest);

            return {
                options: data.map(mapper),
                hasMore: !!links.next,
                additional: {
                    page: page + 1,
                },
            };
        };

/**
 * Wrapper for options loader with filter.
 *
 * @param loader
 * @param filter
 */
export const filteredOptionsLoader = <TOption = ISelectOption>(loader: ILoadOptions<TOption>, filter: (item: TOption | GroupBase<TOption>) => boolean): ILoadOptions<TOption> =>
    async (search, selectOpt, additionalOpt) => {
        const options = await loader(search, selectOpt, additionalOpt);
        return {
            ...options,
            options: options.options.filter(filter),
        };
    };
/**
 * Helper factory for AsyncSelectInput and SelectInput `onChange` event in form.
 * Works in React.FC only.
 *
 * Prerequisites:
 *  - Selection state should has ISelectOption[] type.
 *
 * @param submitValuesSetter Setter for form values state.
 * @param selectionSetter    Setter for selection options state.
 * @param property           Property in form values state which should be updated.
 */
export const createChangeHandler = <S, TOption extends ISelectOption>(
    submitValuesSetter: Dispatch<SetStateAction<S>>,
    selectionSetter: Dispatch<SetStateAction<TOption[]>>,
    property: keyof S
) => (options: TOption[]) => {
        submitValuesSetter((prevState: S) => ({
            ...prevState,
            [property]: options !== null ? options.map((option) => option.value) : [],
        }));
        selectionSetter(options !== null ? [...options] : []);
    };
